import React from "react";
import { Card, Form, Row, Col, Button, Image } from "react-bootstrap";
import { useUploadFile } from "../../../../../api/fileUpload/fileUpload.hooks";

const FAQSection = ({ data, onUpdate }) => {
  const { upload, isLoading: isUploading } = useUploadFile();

  const handleChange = (field, value) => {
    onUpdate({ ...data, [field]: value });
  };

  const handleFileUpload = async (file) => {
    try {
      const res = await upload(file);
      if (res?.url) {
        handleChange("image", res.url);
      }
    } catch (err) {
      console.error("File upload failed:", err);
    }
  };

  const updateFAQ = (faqId, updates) => {
    const faqs = (data.faqs || []).map((faq) =>
      faq.id === faqId ? { ...faq, ...updates } : faq
    );
    handleChange("faqs", faqs);
  };

  const addFAQ = () => {
    const nextId =
      (data.faqs || []).reduce(
        (max, faq) => Math.max(max, Number(faq.id) || 0),
        0
      ) + 1;

    handleChange("faqs", [
      ...(data.faqs || []),
      { id: nextId, title: "", description: "" },
    ]);
  };

  const removeFAQ = (faqId) => {
    handleChange(
      "faqs",
      (data.faqs || []).filter((faq) => faq.id !== faqId)
    );
  };

  return (
    <Card className="mb-4 shadow">
      <Card.Header className="bg-neutral-gray text-dark d-flex justify-content-between align-items-center">
        <h5 className="mb-0 text-dark">FAQ Section</h5>
        <Form.Check
          type="checkbox"
          label="Visible"
          checked={data.isVisible}
          onChange={(e) => handleChange("isVisible", e.target.checked)}
          className="mb-0"
        />
      </Card.Header>
      {data.isVisible && (
        <Card.Body>
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Heading</Form.Label>
                <Form.Control
                  type="text"
                  value={data.heading || ""}
                  onChange={(e) => handleChange("heading", e.target.value)}
                  placeholder="Frequently Asked Questions"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    e.target.files?.[0] && handleFileUpload(e.target.files[0])
                  }
                />
                {isUploading && (
                  <div className="text-muted small mt-1">Uploading...</div>
                )}
                {data.image && (
                  <div className="mt-2">
                    <Image
                      src={data.image}
                      alt="FAQ image preview"
                      thumbnail
                      style={{ maxWidth: "200px" }}
                    />
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <h6 className="fw-bold mb-3">FAQs</h6>
          {(data.faqs || []).map((faq, index) => (
            <div
              key={faq.id}
              className="border rounded p-3 mb-3 bg-light-subtle"
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">FAQ {index + 1}</h6>
                {(data.faqs || []).length > 1 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeFAQ(faq.id)}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  value={faq.title || ""}
                  onChange={(e) =>
                    updateFAQ(faq.id, { title: e.target.value })
                  }
                  placeholder="Question title"
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={faq.description || ""}
                  onChange={(e) =>
                    updateFAQ(faq.id, { description: e.target.value })
                  }
                  placeholder="Answer"
                />
              </Form.Group>
            </div>
          ))}
          <Button variant="outline-primary" onClick={addFAQ}>
            Add FAQ
          </Button>
        </Card.Body>
      )}
    </Card>
  );
};

export default FAQSection;
